/* Shared presentational primitives for the two course pages.

   The site styles components inline rather than with CSS modules (see
   Appointment.js, Navbar/Main.js), so these keep the same approach and reuse
   the same palette: brand #90510F, gold #D4A96A, ink #1A0D05, body #5A4A3A. */

import React from 'react';
import useEurRate from '../../hooks/useEurRate';
import { toEur } from '../../config/prices.mjs';

/* Mirrors the CSS custom properties in src/assets/css/modern.css - keep the
   two in step so inline-styled blocks and class-styled ones look identical. */
export const BRAND = '#90510F';
export const GOLD = '#D4A96A';
export const INK = '#1A0D05';
export const BODY = '#5A4A3A';
export const TEXT = '#4A3728';
export const MUTED = '#8B7B6B';
export const CREAM = '#FAF7F2';
export const CREAM_DEEP = '#F3EEE6';

export const serif = "'Cormorant Garamond', 'PT Serif', serif";
export const sans = "'Inter', sans-serif";

/**
 * Fill {{placeholders}} in a string that came out of a locale array.
 *
 * i18next only interpolates the string it looked up, not strings nested
 * inside an object it returned with { returnObjects: true } - which is how
 * the practical-info rows arrive. This does that last step.
 */
export const interpolate = (template, vars) =>
  String(template).replace(/\{\{(\w+)\}\}/g, (match, key) => (
    Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : match
  ));

/** Page section with consistent vertical rhythm and an optional tinted background. */
export const Section = ({ id, tinted, children, style }) => (
  <section
    id={id}
    style={{
      padding: '72px 0',
      background: tinted ? CREAM : '#fff',
      ...style,
    }}
  >
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 24px' }}>
      {children}
    </div>
  </section>
);

/** Section heading. Always an <h2> - the single <h1> comes from Bredcrumb. */
export const Heading = ({ eyebrow, children, align = 'left' }) => (
  <header style={{ marginBottom: '28px', textAlign: align }}>
    {eyebrow && (
      <p
        style={{
          fontFamily: sans,
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: GOLD,
          marginBottom: '10px',
        }}
      >
        {eyebrow}
      </p>
    )}
    <h2
      style={{
        fontFamily: serif,
        fontSize: 'clamp(1.75rem, 3.4vw, 2.5rem)',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        lineHeight: 1.2,
        color: INK,
        margin: 0,
      }}
    >
      {children}
    </h2>
    <div
      style={{
        width: '40px',
        height: '2px',
        background: BRAND,
        borderRadius: '2px',
        marginTop: '18px',
        marginLeft: align === 'center' ? 'auto' : 0,
        marginRight: align === 'center' ? 'auto' : 0,
      }}
    />
  </header>
);

/** Body paragraph. */
export const P = ({ children, style }) => (
  <p
    style={{
      fontFamily: sans,
      fontSize: '16px',
      lineHeight: 1.85,
      color: BODY,
      marginBottom: '18px',
      ...style,
    }}
  >
    {children}
  </p>
);

/** Numbered list used for the "what you also learn" blocks. */
export const NumberedList = ({ items }) => (
  <ol style={{ paddingLeft: 0, listStyle: 'none', margin: '0 0 18px' }}>
    {items.map((item, i) => (
      <li
        key={i}
        style={{
          display: 'flex',
          gap: '14px',
          fontFamily: sans,
          fontSize: '16px',
          lineHeight: 1.75,
          color: BODY,
          marginBottom: '14px',
        }}
      >
        <span
          style={{
            flexShrink: 0,
            width: '26px',
            height: '26px',
            borderRadius: '50%',
            background: 'rgba(144,81,15,0.10)',
            color: BRAND,
            fontSize: '13px',
            fontWeight: 700,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '2px',
          }}
        >
          {i + 1}
        </span>
        <span>{item}</span>
      </li>
    ))}
  </ol>
);

/** Title + text cards, optionally flagged as new. */
export const FeatureList = ({ items, newBadge }) => (
  <div style={{ display: 'grid', gap: '16px' }}>
    {items.map((item, i) => (
      <div
        key={i}
        style={{
          padding: '20px 22px',
          borderRadius: '16px',
          background: '#fff',
          border: '1px solid rgba(144,81,15,0.14)',
        }}
      >
        <h3
          style={{
            fontFamily: serif,
            fontSize: '20px',
            fontWeight: 600,
            color: INK,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px',
          }}
        >
          {item.title}
          {item.isNew && newBadge && (
            <span
              style={{
                fontFamily: sans,
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: BRAND,
                background: 'rgba(144,81,15,0.10)',
                borderRadius: '9999px',
                padding: '4px 10px',
              }}
            >
              {newBadge}
            </span>
          )}
        </h3>
        {item.text && (
          <p
            style={{
              fontFamily: sans,
              fontSize: '15px',
              lineHeight: 1.75,
              color: BODY,
              margin: '10px 0 0',
            }}
          >
            {item.text}
          </p>
        )}
      </div>
    ))}
  </div>
);

/** Label/value table for the "Praktične informacije" blocks. */
export const InfoTable = ({ rows }) => (
  <dl style={{ margin: 0, display: 'grid', gap: '2px' }}>
    {rows.map((row, i) => (
      <div
        key={i}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px 20px',
          padding: '14px 0',
          borderBottom: i === rows.length - 1 ? 'none' : '1px solid rgba(144,81,15,0.12)',
        }}
      >
        <dt
          style={{
            flex: '0 0 190px',
            fontFamily: sans,
            fontSize: '13px',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: BRAND,
          }}
        >
          {row.label}
        </dt>
        <dd
          style={{
            flex: '1 1 260px',
            fontFamily: sans,
            fontSize: '16px',
            lineHeight: 1.7,
            color: BODY,
            margin: 0,
          }}
        >
          {row.value}
        </dd>
      </div>
    ))}
  </dl>
);

/**
 * Wrapper that frames one or more <PriceTable>s exactly like the /pricing
 * page does: white background, the same 780px `.vb-pricelist` column, and a
 * centred brown serif section title with a rule under it.
 *
 * The generic <Section>/<Heading> pair is deliberately NOT used here - its
 * left-aligned heading and cream background made these blocks read as a
 * different component. Anything price-related on the site should look like
 * the price list.
 */
export const PriceList = ({ title, children, footnote }) => (
  <section style={{ padding: '72px 0', background: '#fff' }}>
    <div className="container">
      <div className="vb-pricelist">
        <section className="vb-price-section">
          <h2 className="vb-price-section-title">{title}</h2>
          {children}
        </section>
        {footnote && <p className="vb-price-footnote">{footnote}</p>}
      </div>
    </div>
  </section>
);

/**
 * Price list, in the salon's house style.
 *
 * It reuses the very same `.vb-price-*` classes as the main /pricing page
 * (defined in src/assets/css/modern.css) rather than restyling the numbers
 * here, so the two can never drift apart: name on the left in --vb-text, a
 * dotted leader, the amount on the right in --vb-brown, and the RSD / EUR
 * columns when the exchange rate has loaded.
 *
 * Prices are passed RAW ("3000", "4500-6000"), exactly as /pricing renders
 * them - text in the DOM, never an image. A competitor publishes their price
 * list as a picture Google cannot read; that is the whole point.
 *
 * @param {Array<{label: string, price: string}>} rows
 */
export const PriceTable = ({ caption, rows, note }) => {
  const eurRate = useEurRate();

  return (
    <div className="vb-price-group">
      {caption && <h3 className="vb-price-subtitle">{caption}</h3>}

      {eurRate && (
        <div className="vb-price-columns">
          <span className="vb-price-column-rsd">RSD</span>
          <span className="vb-price-column-eur">≈ EUR</span>
        </div>
      )}

      <ul className="vb-price-rows">
        {rows.map((row, i) => (
          <li className="vb-price-row" key={i}>
            <span className="vb-price-name">{row.label}</span>
            <span className="vb-price-dots" aria-hidden="true" />
            <span className="vb-price-value">{row.price}</span>
            {eurRate && (
              <span className="vb-price-value-eur">{toEur(row.price, eurRate)}</span>
            )}
          </li>
        ))}
      </ul>

      {note && <p className="vb-price-note">{note}</p>}
    </div>
  );
};

/**
 * Side-by-side comparison ("natural vs synthetic hair", "which treatment do
 * I need"). Plain content, not a data grid.
 *
 * All of the styling lives in the .vb-compare rules in modern.css, because
 * the global `th { background: brown !important }` there can only be undone
 * by a stylesheet rule - inline styles lose to !important.
 */
export const CompareTable = ({ columns, rows }) => (
  <div style={{ overflowX: 'auto', margin: '0 0 20px' }}>
    <table className="vb-compare">
      <thead>
        <tr>
          <td />
          {columns.map((col, i) => <th key={i} scope="col">{col}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            <th scope="row">{row.label}</th>
            {row.values.map((value, j) => <td key={j}>{value}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** Highlighted callout (early-bird promo, "the course pays for itself"). */
export const Callout = ({ children, tone = 'brand' }) => (
  <div
    style={{
      marginTop: '24px',
      padding: '22px 26px',
      borderRadius: '18px',
      background: tone === 'brand' ? 'rgba(144,81,15,0.07)' : 'rgba(212,169,106,0.14)',
      borderLeft: `3px solid ${tone === 'brand' ? BRAND : GOLD}`,
      fontFamily: sans,
      fontSize: '16px',
      lineHeight: 1.75,
      fontWeight: 600,
      color: INK,
    }}
  >
    {children}
  </div>
);

/** Pill button, matching the one used throughout the site. */
export const buttonStyle = (variant = 'solid') => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  padding: '15px 34px',
  background: variant === 'solid' ? BRAND : 'transparent',
  color: variant === 'solid' ? '#fff' : BRAND,
  border: variant === 'solid' ? 'none' : `1.5px solid ${BRAND}`,
  fontFamily: sans,
  fontSize: '13px',
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  borderRadius: '9999px',
  textDecoration: 'none',
  cursor: 'pointer',
  boxShadow: variant === 'solid' ? '0 8px 24px rgba(144,81,15,0.30)' : 'none',
  transition: 'background 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease',
});
